import { useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import { selectAuthData } from '@/containers/Login/AuthSelectors'
import useBusinessType from '@/shared/hooks/useBusinessType'
import getIcon from '../helpers/getIcon'
import { resolveRedesignedMenu } from '../helpers/resolveRedesignedMenu'
import type { ResolvedMenuEntry, ResolvedMenuFamilyItem } from '../helpers/resolveRedesignedMenu'
import { CURRENT_VERTICAL_LABEL_KEY, LOGOUT_LABEL_KEY, VERTICAL_LABEL_KEYS, VERTICALS_FAMILY } from '../redesignedMenu'
import useSidebarLogout from '../useSidebarLogout'
import type {
  MenuEntryView,
  MenuFamilyItemView,
  MenuIcon,
  MenuLogoutView,
  MenuSectionView,
  SidebarAuthData,
} from './types'

interface UseRedesignedSidebarMenuParams {
  isAdmin: boolean
  isSelectraAdmin: boolean
  error: boolean
  applicationType: string
}

const menuIcon = (key: string) => getIcon(key) as MenuIcon

const VERTICAL_HOMES = Object.keys(VERTICAL_LABEL_KEYS).map(vertical => `/${vertical}`)

const isCurrentPage = (to: string, pathname: string) =>
  pathname === to || (!VERTICAL_HOMES.includes(to) && pathname.startsWith(`${to}/`))

const useRedesignedSidebarMenu = ({
  isAdmin,
  isSelectraAdmin,
  error,
  applicationType,
}: UseRedesignedSidebarMenuParams) => {
  const { t } = useTranslation()
  const onLogout = useSidebarLogout()
  const authData: SidebarAuthData = useSelector(selectAuthData)
  const { getApplicationMenuRoutes, getModulesStatuses, availableVerticals } = useBusinessType({
    loaderRoutesEnergy: authData.loaderRoutesEnergy,
    loaderRoutes: authData.loaderRoutes,
    loaderRoutesInsurance: authData.loaderRoutesInsurance,
    modulesStatuses: authData.modulesStatuses,
    modulesStatusesEnergy: authData.modulesStatusesEnergy,
    modulesStatusesInsurance: authData.modulesStatusesInsurance,
  })
  const [openFamilyId, setOpenFamilyId] = useState<string | null>(null)
  const { pathname } = useLocation()

  const menu = resolveRedesignedMenu({
    routes: getApplicationMenuRoutes(applicationType),
    modulesStatus: getModulesStatuses(applicationType),
    country: authData.country,
    isAdmin,
    isSelectraAdmin,
    isExternal: authData.isExternal ?? false,
    commonData: authData.commonData ?? {},
    availableVerticals,
    currentVertical: applicationType,
    error,
  })

  const closeFlyouts = () => setOpenFamilyId(null)

  const currentVerticalLabel = t(CURRENT_VERTICAL_LABEL_KEY)

  const toItemView = (item: ResolvedMenuFamilyItem): MenuFamilyItemView => {
    const label = t(item.labelKey)
    const base = { id: item.id, to: item.to, label, icon: menuIcon(item.icon), current: item.current }
    if (!item.current) return { ...base, ariaLabel: label, note: null }

    return { ...base, ariaLabel: `${label} (${currentVerticalLabel})`, note: currentVerticalLabel }
  }

  const toEntryView = (entry: ResolvedMenuEntry): MenuEntryView => {
    if (entry.kind === 'link') {
      return {
        kind: 'link',
        id: entry.id,
        to: entry.to,
        label: t(entry.labelKey),
        icon: menuIcon(entry.icon),
        selected: isCurrentPage(entry.to, pathname),
        onMouseEnter: closeFlyouts,
      }
    }

    return {
      kind: 'family',
      id: entry.id,
      label: t(entry.labelKey),
      icon: menuIcon(entry.icon),
      items: entry.items.map(toItemView),
      selected: entry.id !== VERTICALS_FAMILY.id && entry.items.some(item => isCurrentPage(item.to, pathname)),
      open: openFamilyId === entry.id,
      onOpen: () => setOpenFamilyId(entry.id),
      onClose: () => setOpenFamilyId(current => (current === entry.id ? null : current)),
    }
  }

  const sections: MenuSectionView[] = menu.sections.map((section, index) => ({
    key: section.key,
    withDivider: index > 0,
    entries: section.entries.map(toEntryView),
  }))

  const logout: MenuLogoutView | null = menu.showLogout
    ? { label: t(LOGOUT_LABEL_KEY), icon: menuIcon('logout'), onClick: onLogout, onMouseEnter: closeFlyouts }
    : null

  return {
    sections,
    logout,
    country: authData.country,
    fill: isAdmin,
  }
}

export default useRedesignedSidebarMenu
